import { isHotkey } from 'is-hotkey';
import React, { useEffect, useMemo, useState } from 'react';
import { styled } from 'styled-components';

import Popover from '@fabric-app/ui/components/Popover/Popover';

import { usePillAutocomplete } from '../hooks/usePillAutocomplete';
import { searchBarFilterConfigs } from '../searchbar.config';
import type {
  SearchBarFilter,
  SearchBarFilterOption,
} from '@fabric-app/business/search/searchbar.types';
import { findComboboxSelectedElement } from '@fabric-app/business/search/utils/elements';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import { ChevronRightIcon } from '@radix-ui/react-icons';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { chainShortcutHandlers } from '@fabric-app/business/keyboardShortcuts/utils';
import { useQueryFolders } from '@fabric-app/business/resources/queries/useQueryFolders';

const FilterComboboxInput = styled.input`
  display: flex;
  height: 30px;
  padding: 2px 8px 2px 10px;
  align-items: center;
  gap: 10px;
  border-radius: 0px 8px 0px 0px;
  border: 1px solid ${cssVar['color-border-primary']};
  background: ${cssVar['color-bg-primary']};
  width: 100%;

  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: 21px; /* 161.538% */

  &::placeholder {
    color: ${cssVar['color-text-placeholder']};
  }
  &:focus {
    outline: none;
  }
`;

const FilterComboBoxInvisibleSpan = styled.span`
  font-size: 13px;
  font-weight: 500;
  line-height: 21px;
  width: 172px;
  padding: 2px 8px 2px 10px;
  flex-shrink: 0;
  white-space: nowrap;

  position: absolute;
  pointer-events: none;
  opacity: 0;
`;

const FilterComboBoxTrigger = styled(Popover.Trigger)`
  display: inline-flex;
  height: 30px;
  flex-grow: 1;
  min-width: 10px;
  color: ${cssVar['color-text-secondary']};
`;

const FilterComboBoxListInner = styled.div`
  padding: 6px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;

  width: 172px;
  height: 100%;
  overflow: hidden;
`;

const FilterComboBoxList = styled(Popover.Content).attrs((props) => {
  return {
    children: (
      <ScrollArea.Default scrollbarVariant="subtle">
        <FilterComboBoxListInner>{props.children}</FilterComboBoxListInner>
      </ScrollArea.Default>
    ),
  };
})`
  width: 172px;
  max-height: min(var(--radix-popover-content-available-height), 400px);

  display: flex;

  padding: 0;

  border-radius: 0px 0px 8px 8px;
  border: 1px solid ${cssVar['color-border-primary']};
  border-top: none;
  background: ${cssVar['color-bg-primary']};

  --base-font-size: 13px;

  font-size: var(--base-font-size);
  font-weight: 500;

  /* soft-dashboard-card */
  box-shadow:
    0px 4px 4px 0px rgba(128, 128, 128, 0.04),
    0px 4px 25px 0px rgba(128, 128, 128, 0.1);

  ${ScrollArea.Root}, ${ScrollArea.Viewport} {
    width: 100%;
    height: 100%;

    // Max height required here or everything breaks,
    // the auto height is not working as expected
    max-height: min(var(--radix-popover-content-available-height), 400px);
  }

  ${mediaMobile} {
    --base-font-size: 14px;
  }
`;

const FilterComboBoxChildList = styled(FilterComboBoxList)`
  border-radius: 8px;
`;

const FilterComboboxListItem = styled.button`
  display: flex;
  padding: 4px 6px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  width: 100%;
  border: none;
  background: none;

  > span {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
  border-radius: 4px;

  &[aria-selected='true'] {
    background: ${cssVar['color-bg-tertiary']};
  }

  ${mediaMobile} {
    padding: 8px;
  }
`;

const FilterComboboxListLabel = styled.span`
  font-size: var(--base-font-size);
  font-weight: 500;
  color: ${cssVar['color-text-tertiary']};

  display: flex;
  padding: 4px;
  justify-content: space-between;
  align-items: center;
`;

const FilterComboboxNestChevron = styled(ChevronRightIcon)`
  width: 0.7rem;
  height: 0.7rem;
`;

const preventDefault = (e: Event) => {
  e.preventDefault();
};

type FilterComboboxProps = {
  onBlur?: (e?: React.FocusEvent<HTMLInputElement>) => void;
  onEscape?: (e?: React.KeyboardEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;

  filter: SearchBarFilter;
  onChange: (value: SearchBarFilterOption) => void;
};

const FilterCombobox: React.FC<FilterComboboxProps> = ({
  onBlur,
  onEscape,
  autoFocus,
  filter,
  onChange,
}) => {
  const [inputValue, setInputValue] = useState<string>(filter.value?.label ?? '');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const spanRef = React.useRef<HTMLSpanElement>(null);
  const controlId = React.useRef<string>(`filter-combobox-${filter.$key}`);
  const { isMobileView } = useResponsive();

  const onOptionSelected = (option: SearchBarFilterOption) => {
    onChange(option);
    setInputValue(option.label);
  };

  const { options, isLoading } = usePillAutocomplete(filter.type, inputValue);

  useEffect(() => {
    if (!inputRef.current || !spanRef.current) return;

    const input = inputRef.current;
    const span = spanRef.current;

    const newWidth = `${span.offsetWidth}px`;
    input.style.minWidth = newWidth;
    input.style.width = newWidth;
  }, [inputValue]);

  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  const onKeyDownHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    chainShortcutHandlers(
      e,
      [
        /**
         * Handle the up arrow key to navigate up the list.
         */
        (e) => {
          if (!isHotkey('up', e)) return false;

          // go to the next item within the listRef (role=option), if none is selected, select the last item
          // if the first item is selected, select the last item (looping)

          const selected =
            findComboboxSelectedElement(controlId.current) ??
            listRef.current?.querySelector('[role="option"][data-option-value]:first-child');

          if (!selected) return false;

          const nextElement =
            selected.previousElementSibling ??
            listRef.current?.querySelector('[role="option"][data-option-value]:last-child');
          if (!nextElement) return false;

          nextElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          const optionValue = nextElement.getAttribute('data-option-value');

          setSelectedItem(optionValue);

          return true;
        },
        /**
         * Handle the down arrow key to navigate down the list.
         */
        (e) => {
          if (!isHotkey('down', e)) return false;

          const selected =
            findComboboxSelectedElement(controlId.current) ??
            listRef.current?.querySelector('[role="option"][data-option-value]:last-child');

          if (!selected) return false;

          const nextElement =
            selected.nextElementSibling ??
            listRef.current?.querySelector('[role="option"][data-option-value]:first-child');

          if (!nextElement) return false;

          const optionValue = nextElement.getAttribute('data-option-value');
          nextElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

          setSelectedItem(optionValue);

          return true;
        },
        /**
         * Handle the right key to expand children if any.
         */
        (e) => {
          if (!isHotkey('right', e)) return false;

          // after the preliminary checks we now get the dom element, and try to move selection
          // down to the first child of the aria-controls list element associated

          const selectedElement = findComboboxSelectedElement(controlId.current);

          if (!selectedElement) {
            return false;
          }

          const childListId = selectedElement.getAttribute('aria-controls');

          if (!childListId) {
            return false;
          }

          const childList = document.getElementById(childListId);

          if (!childList) {
            return false;
          }

          const firstChild = childList.querySelector('[role="option"]');
          if (!firstChild) {
            return false;
          }

          const optionValue = firstChild.getAttribute('data-option-value');

          setSelectedItem(optionValue);

          return true;
        },
        /**
         * Handle the enter key to select the item.
         */
        (e) => {
          if (!isHotkey('enter', e)) return false;

          // recreate the option object from the selected item
          const selectedElement = findComboboxSelectedElement(controlId.current);

          if (!selectedElement) {
            return false;
          }

          const value = selectedElement.getAttribute('data-option-value');
          const label = selectedElement.getAttribute('data-option-label');

          if (!value || !label) {
            return false;
          }

          const selectedOption: SearchBarFilterOption = {
            value,
            label,
          };

          onOptionSelected(selectedOption);

          return true;
        },
        /**
         * Handle the left key to go back to the parent. If possible.
         */
        (e) => {
          if (!isHotkey('left', e)) return false;

          // after the preliminary checks we now get the dom element, and try to move selection
          // up to the parent of the aria-controls list element associated

          const selectedElement = findComboboxSelectedElement(controlId.current);

          if (!selectedElement) {
            return false;
          }

          const parentList = selectedElement.closest('[data-control-id]');

          if (!parentList) {
            return false;
          }

          const controlledBy = document.querySelector('[aria-controls="' + parentList.id + '"]');
          if (!controlledBy) {
            return false;
          }

          const optionValue = controlledBy.getAttribute('data-option-value');

          setSelectedItem(optionValue);

          return true;
        },
        /**
         * Handle the escape key to close the combobox.
         */
        (e) => {
          if (!isHotkey('escape', e)) return false;

          onEscape?.();

          return true;
        },
      ],
      {
        preventDefaultOnHandle: true,
        stopPropagationOnHandle: true,
      },
    );
  };

  useEffect(() => {
    const onFocus = (e: FocusEvent) => {
      if (e.target === inputRef.current) return;

      // if it's an element inside the list, focus the input back
      if (
        e.target instanceof Element &&
        e.target.closest(`[data-control-id="${controlId.current}"]`)
      ) {
        inputRef.current?.focus();
        return;
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      if (
        e.target instanceof Element &&
        (e.target.closest(`[data-control-id="${controlId.current}"]`) ||
          e.target === inputRef.current)
      )
        return;

      if (isMobileView && selectedItem !== null) {
        setSelectedItem(null);
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      onBlur?.();
    };

    document.addEventListener('focus', onFocus, true);
    document.addEventListener('pointerup', onPointerUp);

    return () => {
      document.removeEventListener('focus', onFocus, true);
      document.removeEventListener('pointerup', onPointerUp);
    };
  }, [onBlur, selectedItem, isMobileView]);

  const onInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.currentTarget.value);
  };

  const onClickOption = (e: React.MouseEvent<HTMLButtonElement>, option: SearchBarFilterOption) => {
    e.preventDefault();

    if (option.canHaveChildren && selectedItem !== option.value) {
      setSelectedItem(option.value);
      return;
    }

    onOptionSelected(option);
  };

  const onHoverOption = (e: React.MouseEvent<HTMLButtonElement>) => {
    const optionValue = e.currentTarget.getAttribute('data-option-value');
    setSelectedItem(optionValue);
  };

  return (
    <Popover open={true}>
      <FilterComboBoxTrigger tabIndex={-1}>
        <FilterComboboxInput
          placeholder={
            isMobileView ? `Filter ${searchBarFilterConfigs[filter.type].name}` : 'Filter by name'
          }
          autoFocus={autoFocus}
          onKeyDown={onKeyDownHandler}
          role="combobox"
          ref={inputRef}
          value={inputValue}
          onChange={onInputChanged}
        />
        <FilterComboBoxInvisibleSpan ref={spanRef}>{inputValue}</FilterComboBoxInvisibleSpan>
      </FilterComboBoxTrigger>

      <FilterComboBoxList
        side="bottom"
        onPointerDownOutside={preventDefault}
        onEscapeKeyDown={preventDefault}
        onFocusOutside={preventDefault}
        tab-index={-1}
        ref={listRef}
        data-control-id={controlId.current}
        sticky={isMobileView ? 'always' : 'partial'}
      >
        {isLoading && <FilterComboboxListLabel>Loading...</FilterComboboxListLabel>}
        {!isLoading && options.length === 0 && (
          <FilterComboboxListLabel>No results found</FilterComboboxListLabel>
        )}

        {options.map((option) => (
          <FilterComboboxOption
            key={option.value}
            onHover={onHoverOption}
            option={option}
            onClick={onClickOption}
            selectedItem={selectedItem}
            controlId={controlId.current}
          />
        ))}
      </FilterComboBoxList>
    </Popover>
  );
};

interface FilterComboboxOptionProps {
  option: SearchBarFilterOption;
  onClick: (e: React.MouseEvent<HTMLButtonElement>, option: SearchBarFilterOption) => void;
  onHover?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  selectedItem: string | null;
  controlId?: string;
  ancestorValues?: string[];
}

const FilterComboboxOption: React.FC<FilterComboboxOptionProps> = ({
  option,
  onClick,
  onHover,
  selectedItem,
  controlId,
  ancestorValues,
}) => {
  const isSelected = selectedItem === option.value;
  const [childrenExpanded, setChildrenExpanded] = useState(false);
  const { isMobileView } = useResponsive();

  const { folders: folders, isLoading: isLoadingFolders } = useQueryFolders(option.value, {
    enabled: childrenExpanded && !!option.canHaveChildren,
  });

  useEffect(() => {
    if (!option.canHaveChildren || !controlId || isSelected) {
      setChildrenExpanded(isSelected);
      return;
    }

    const selectedElement = findComboboxSelectedElement(controlId);

    if (!selectedElement) {
      setChildrenExpanded(false);
      return;
    }

    const ancestors = selectedElement.getAttribute('data-option-ancestor-values')?.split(',');

    if (!ancestors) {
      setChildrenExpanded(false);
      return;
    }

    setChildrenExpanded(ancestors.includes(option.value));
  }, [controlId, isSelected, option.canHaveChildren, option.value, selectedItem]);

  const item = (
    <FilterComboboxListItem
      role="option"
      data-option-value={option.value}
      data-option-label={option.label}
      // there is possibly a better solution, but this was the most efficient
      data-option-ancestor-values={ancestorValues?.join(',')}
      aria-selected={isSelected}
      onClick={(e) => onClick(e, option)}
      onMouseEnter={onHover}
      onMouseMove={onHover}
      tabIndex={-1}
    >
      <span>{option.label}</span>
      {option.canHaveChildren && <FilterComboboxNestChevron />}
    </FilterComboboxListItem>
  );

  const childOptions = useMemo(() => {
    if (!folders) return null;

    return folders.map((folder) => ({
      value: folder.id,
      label: folder.name || 'Untitled folder',
      canHaveChildren: true,
    }));
  }, [folders]);

  const childAncestors = useMemo(() => {
    return [...(ancestorValues ?? []), option.value];
  }, [ancestorValues, option.value]);

  if (option.canHaveChildren) {
    return (
      <Popover open={childrenExpanded}>
        <FilterComboBoxTrigger asChild>{item}</FilterComboBoxTrigger>

        <FilterComboBoxChildList
          side={isMobileView ? 'bottom' : 'right'}
          align="start"
          onOpenAutoFocus={preventDefault}
          onCloseAutoFocus={preventDefault}
          data-control-id={controlId}
          sticky={isMobileView ? 'always' : 'partial'}
        >
          {isLoadingFolders && <FilterComboboxListLabel>Loading...</FilterComboboxListLabel>}
          {!isLoadingFolders && childOptions?.length === 0 && (
            <FilterComboboxListLabel>No subfolders found</FilterComboboxListLabel>
          )}

          {childOptions?.map((childOption) => (
            <FilterComboboxOption
              key={childOption.value}
              option={childOption}
              onClick={onClick}
              onHover={onHover}
              selectedItem={selectedItem}
              controlId={controlId}
              ancestorValues={childAncestors}
            />
          ))}
        </FilterComboBoxChildList>
      </Popover>
    );
  } else {
    return item;
  }
};

export default FilterCombobox;
